import { useEffect, useRef, useCallback, useState } from 'react';
import { io } from 'socket.io-client';
import { getAuth } from 'firebase/auth';

const SOCKET_URL = process.env.NODE_ENV === 'production' 
  ? (process.env.REACT_APP_SOCKET_URL || 'https://your-production-domain.com')
  : 'http://localhost:5000';

const RECONNECTION_ATTEMPTS = 5;
const RECONNECTION_DELAY = 1000;
const TYPING_DEBOUNCE = 1000;
const ACK_TIMEOUT = 8000; // 🚀 OPTIMIZED: 8 second ACK timeout for better UX

export const useSocket = (options = { enabled: true }) => {
  const { enabled = true } = options;
  const socketRef = useRef(null);
  const auth = getAuth();
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState(null);
  const typingTimeoutRef = useRef(null);
  const messageQueueRef = useRef([]);
  const processingQueueRef = useRef(false);

  // ✨ DEDUPE: Remember messages we've already surfaced to the UI
  const seenMsgKeysRef = useRef(new Set());

  // Build a stable dedupe key for any message shape
  const buildMsgKey = (m) => {
    // Prefer explicit IDs first
    if (m?.clientMessageId) return `cmid:${m.clientMessageId}`;
    if (m?.id) return `id:${m.id}`;

    // Telegram-specific: if server sends telegram ids, construct a deterministic key
    if (m?.source === 'telegram' && (m?.telegramMessageId || m?.telegram_chat_message_id)) {
      const chatId = m.telegramChatId || m.telegram_chat_id || m.roomId || '';
      const mid = m.telegramMessageId || m.telegram_chat_message_id;
      return `tg:${chatId}:${mid}`;
    }

    // Fallback: content + sender + coarse timestamp bucket (3s)
    const from = m?.from || m?.sender || '';
    const text = typeof m?.text === 'string' ? m.text : (m?.text?.sanitized || '');
    const ts = m?.timestamp || m?.createdAt || m?.time || Date.now();
    const t = typeof ts === 'number' ? ts : (ts?.toMillis ? ts.toMillis() : (new Date(ts)).getTime());
    const bucket = Math.floor((t || Date.now()) / 3000); // 3s buckets
    return `fx:${from}:${text}:${bucket}`;
  };

  // Normalize message: attach a clientMessageId if we can derive one
  const normalizeIncomingMsg = (m) => {
    if (!m || typeof m !== 'object') return m;

    // If this is a Telegram message and lacks clientMessageId, synthesize one
    if (!m.clientMessageId && m.source === 'telegram') {
      if (m.telegramMessageId || m.telegram_chat_message_id) {
        const chatId = m.telegramChatId || m.telegram_chat_id || m.roomId || '';
        const mid = m.telegramMessageId || m.telegram_chat_message_id;
        m.clientMessageId = `tg_${chatId}_${mid}`;
      }
    }

    // As a final fallback, tag a deterministic key so ChatPanel can dedupe
    if (!m.clientMessageId) {
      m.clientMessageId = buildMsgKey(m);
    }
    return m;
  };

  // 🚀 HEARTBEAT & AUTO-REJOIN: Track rooms and heartbeat
  const joinedRoomsRef = useRef(new Set());
  const heartbeatIntervalRef = useRef(null);
  const missedHeartbeatsRef = useRef(0);

  // 🚀 INFLIGHT ACK CAPPING: Prevent too many concurrent sends
  const MAX_INFLIGHT = 3;
  const inFlightRef = useRef(0);

  // 🚀 TELEMETRY: Track ACK latency for performance monitoring
  const ackHistRef = useRef([]);
  
  // 🚀 UTILITY: Calculate percentile for telemetry
  const percentile = (arr, p) => {
    if (arr.length === 0) return 0;
    const sorted = [...arr].sort((a, b) => a - b);
    const index = Math.ceil((p / 100) * sorted.length) - 1;
    return sorted[Math.max(0, index)];
  };

  // 🚀 EXPONENTIAL BACKOFF WITH JITTER: Calculate backoff delay to avoid herding
  const calculateBackoff = useCallback((retryCount) => {
    const baseDelay = Math.min(16000, 500 * Math.pow(2, retryCount));
    const jitter = Math.random() * 200; // Add up to 200ms jitter
    return baseDelay + jitter;
  }, []);

  // 🚀 ENHANCED QUEUE PROCESSING: With exponential backoff and better error handling
  const processMessageQueue = useCallback(async () => {
    if (processingQueueRef.current || messageQueueRef.current.length === 0) return;

    processingQueueRef.current = true;
    // Processing queued messages

    const queueCopy = [...messageQueueRef.current];
    const successfulMessages = [];
    const failedMessages = [];

    for (const item of queueCopy) {
      try {
        // 🚀 EXPONENTIAL BACKOFF: Apply backoff before retry
        if (item.retryCount > 0) {
          const backoffDelay = calculateBackoff(item.retryCount);
          // Waiting before retry
          await new Promise(resolve => setTimeout(resolve, backoffDelay));
        }

        await sendMessage(item.content, item.roomId, item.csrfToken, item.clientMessageId);
        successfulMessages.push(item);
        
        // Small delay between messages to avoid overwhelming the server
        await new Promise(resolve => setTimeout(resolve, 100));
        
      } catch (error) {
        item.retryCount = (item.retryCount || 0) + 1;
        
        // Remove from queue if max retries exceeded
        if (item.retryCount >= 5) {
          continue;
        }
        
        failedMessages.push(item);
      }
    }

    // Update queue with failed messages only
    messageQueueRef.current = failedMessages;
    
    // Queue processing complete
    processingQueueRef.current = false;

    // Continue processing if there are still failed messages
    if (failedMessages.length > 0) {
      setTimeout(() => processMessageQueue(), 1000);
    }
  }, [calculateBackoff]);

  // 🚀 CAPPED SEND MESSAGE: With inflight limiting, ACK timeout, and telemetry
  const sendMessageCapped = useCallback(async (content, roomId) => {
    // 🚀 BACKPRESSURE: Wait if too many inflight sends
    while (inFlightRef.current >= MAX_INFLIGHT) {
      await new Promise(resolve => setTimeout(resolve, 20));
    }
    
    inFlightRef.current++;
    
    try {
      const t0 = performance.now();
      const result = await sendMessage(content, roomId);
      
      // 🚀 TELEMETRY: Track ACK latency
      const dt = performance.now() - t0;
      ackHistRef.current.push(dt);
      
      // Log percentiles every 25 messages
      if (ackHistRef.current.length % 25 === 0) {
        const p50 = percentile(ackHistRef.current, 50);
        const p95 = percentile(ackHistRef.current, 95);

        
        // Keep only last 100 measurements to prevent memory growth
        if (ackHistRef.current.length > 100) {
          ackHistRef.current = ackHistRef.current.slice(-100);
        }
      }
      
      return result;
    } finally {
      inFlightRef.current--;
    }
  }, []);

  // 🚀 ENHANCED SEND MESSAGE: With ACK timeout, idempotency, and better queue management
  const sendMessage = useCallback((content, roomId, csrfToken, clientMessageId) => {
    return new Promise((resolve, reject) => {
      // Check if socket exists and is connected
      if (!socketRef.current) {
        const queuedMessage = { 
          content, 
          roomId, 
          csrfToken,
          clientMessageId,
          retryCount: 0,
          id: Date.now() + Math.random(),
          timestamp: Date.now()
        };
        messageQueueRef.current.push(queuedMessage);
        return resolve(); // resolve even if queued
      }
      
      if (!socketRef.current.connected) {
        // 🚀 IMPROVED QUEUE: Add retry count and unique ID
        const queuedMessage = { 
          content, 
          roomId, 
          csrfToken,
          clientMessageId,
          retryCount: 0,
          id: Date.now() + Math.random(),
          timestamp: Date.now()
        };
        messageQueueRef.current.push(queuedMessage);
        // Message queued offline
        return resolve(); // resolve even if queued
      }

      let timedOut = false;
      const timeoutId = setTimeout(() => {
        timedOut = true;
        reject(new Error('Message ACK timeout'));
      }, ACK_TIMEOUT);

      socketRef.current.emit('send_message', { content, roomId, csrfToken, clientMessageId }, (ack) => {
        if (timedOut) return; // Already rejected due to timeout
        
        clearTimeout(timeoutId);
        
        if (ack?.error) {
          reject(new Error(ack.error));
        } else {
          resolve();
        }
      });
    });
  }, []);

  // connect uses onAuthStateChanged and unsubscribes on cleanup
  const connect = useCallback(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (!user) {
        setError("User not authenticated");
        return;
      }
      // Always fetch a fresh token
      const token = await user.getIdToken();

      socketRef.current = io(SOCKET_URL, {
        auth: { token },
        reconnection: true,
        reconnectionAttempts: RECONNECTION_ATTEMPTS,
        reconnectionDelay: RECONNECTION_DELAY,
        timeout: 10000,
        transports: ['websocket', 'polling'],
      });

      socketRef.current.on('connect', () => {
        setIsConnected(true);
        setError(null);
        // Socket connected successfully
        
        // 🚀 PROCESS QUEUE: Process queued messages when connected
        if (messageQueueRef.current.length > 0) {
          // Processing queued messages on connection
          processMessageQueue();
        }
      });

      socketRef.current.on('connect_error', (error) => {
        setIsConnected(false);
        setError(`Connection failed: ${error.message}`);
      });

      socketRef.current.on('disconnect', (reason) => {
        setIsConnected(false);
        // Socket disconnected
      });
      
      socketRef.current.on('connect_error', (error) => {
        setError('Connection failed. Retrying...');
      });

      socketRef.current.on('error', (error) => {
        setError(error.message || 'An error occurred');
      });

        // 🚀 NEW: Handle reconnection events
  socketRef.current.on('reconnect', (attemptNumber) => {
            // Socket reconnected
    setIsConnected(true);
    setError(null);
    
            // Process queued messages after reconnection
        if (messageQueueRef.current.length > 0) {
          // Processing queued messages after reconnection
          processMessageQueue();
        }

        // 🚀 AUTO-REJOIN: Rejoin all previously joined rooms
        joinedRoomsRef.current.forEach(roomId => {
          // Auto-rejoining room
          socketRef.current.emit('join_room', roomId);
        });
  });

  socketRef.current.on('reconnect_attempt', (attemptNumber) => {
            // Socket reconnection attempt
  });

        socketRef.current.on('reconnect_failed', () => {
        setError('Connection failed permanently');
      });

      // 🚀 HEARTBEAT: Send heartbeat every 25 seconds
      heartbeatIntervalRef.current = setInterval(() => {
        if (socketRef.current?.connected) {
          socketRef.current.emit('ping', Date.now());
        }
      }, 25000);

      // 🚀 HEARTBEAT RESPONSE: Reset missed count on pong
      socketRef.current.on('pong', () => {
        missedHeartbeatsRef.current = 0;
      });

      // 🚀 MISSED HEARTBEAT: Track missed heartbeats
      socketRef.current.on('missed_heartbeat', () => {
        missedHeartbeatsRef.current++;
        if (missedHeartbeatsRef.current >= 2) {
          socketRef.current.disconnect();
        }
      });
    });

    return () => unsubscribe();
  }, [processMessageQueue]);

  const disconnect = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.disconnect();
      setIsConnected(false);
    }
    
    // 🚀 CLEANUP: Clear heartbeat interval
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }
  }, []);

  const joinRoom = useCallback((roomId) => {
    if (!socketRef.current?.connected) {
      return;
    }
    socketRef.current.emit('join_room', roomId);
    joinedRoomsRef.current.add(roomId);
  }, []);

  const sendTypingStatus = useCallback((roomId, isTyping) => {
    if (!socketRef.current?.connected) return;

    clearTimeout(typingTimeoutRef.current);
    socketRef.current.emit('typing', { roomId, isTyping });

    if (isTyping) {
      typingTimeoutRef.current = setTimeout(() => {
        socketRef.current.emit('typing', { roomId, isTyping: false });
      }, TYPING_DEBOUNCE);
    }
  }, []);

  const loadMoreMessages = useCallback((roomId, lastMessageId) => {
    if (!socketRef.current?.connected) return;
    socketRef.current.emit('load_more_messages', { roomId, lastMessageId });
  }, []);

  const onMessage = useCallback((callback) => {
    if (!socketRef.current) return;

    // 🔧 FIX 2: Single handler for all events with proper deduplication
    const handler = (payload) => {
      const items = Array.isArray(payload) ? payload : [payload];
      const deliver = [];

      for (const raw of items) {
        const msg = normalizeIncomingMsg({ ...raw });      // synthesize clientMessageId for TG
        const key = buildMsgKey(msg);                      // stable key from cmid/id/TG ids/text/timestamp


        if (seenMsgKeysRef.current.has(key)) {
          // Duplicate message dropped
          continue;     // DROP duplicates
        }
        seenMsgKeysRef.current.add(key);
        deliver.push(msg);
      }

      if (deliver.length === 0) return;
      
      // Pass through either a single message or an array, matching original shape
      if (Array.isArray(payload)) {
        callback(deliver);
      } else {
        callback(deliver[0]);
      }
    };

    // 🔧 Subscribe all events to the same deduplication handler
    socketRef.current.on('receive_message', handler);
    socketRef.current.on('initial_messages', handler);
    socketRef.current.on('more_messages', handler);

    return () => {
      socketRef.current?.off('receive_message', handler);
      socketRef.current?.off('initial_messages', handler);
      socketRef.current?.off('more_messages', handler);
    };
  }, []);

  const onTyping = useCallback((callback) => {
    if (!socketRef.current) return;
    socketRef.current.on('user_typing', callback);
    return () => socketRef.current.off('user_typing', callback);
  }, []);

  const onError = useCallback((callback) => {
    if (!socketRef.current) return;
    socketRef.current.on('error', callback);
    return () => socketRef.current.off('error', callback);
  }, []);

  // 🚀 NEW: Get queue statistics
  const getQueueStats = useCallback(() => {
    return {
      queueLength: messageQueueRef.current.length,
      isProcessing: processingQueueRef.current,
      oldestMessage: messageQueueRef.current.length > 0 
        ? Math.min(...messageQueueRef.current.map(m => m.timestamp))
        : null
    };
  }, []);

  // 🚀 NEW: Clear message queue
  const clearMessageQueue = useCallback(() => {
    messageQueueRef.current = [];
    // Message queue cleared
  }, []);

  // 🚀 UTILITY: Get connection status
  const getConnectionStatus = useCallback(() => {
    return {
      connected: socketRef.current?.connected || false,
      error: error,
      queueLength: messageQueueRef.current.length,
      processingQueue: processingQueueRef.current,
      seenMessageKeys: seenMsgKeysRef.current.size
    };
  }, [error]);

  // 🚀 CLEANUP: Clear seen message keys periodically to prevent memory bloat
  useEffect(() => {
    const cleanupInterval = setInterval(() => {
      if (seenMsgKeysRef.current.size > 1000) {
        // Cleaning up seen message keys cache
        seenMsgKeysRef.current.clear();
      }
    }, 300000); // Clean up every 5 minutes if needed

    return () => clearInterval(cleanupInterval);
  }, []);

  // useEffect cleans up unsubscribe
  useEffect(() => {
    if (!enabled) return () => {};
    const unsubscribe = connect();
    // Close socket on pagehide to keep page eligible for bfcache
    const onPageHide = () => {
      disconnect();
    };
    // Reconnect on pageshow after bfcache restore or navigation back
    const onPageShow = () => {
      connect();
    };
    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('pageshow', onPageShow);
    return () => {
      disconnect();
      clearTimeout(typingTimeoutRef.current);
      if (unsubscribe) unsubscribe();
      window.removeEventListener('pagehide', onPageHide);
      window.removeEventListener('pageshow', onPageShow);
    };
  }, [connect, disconnect, enabled]);

  return {
    connect,
    disconnect,
    joinRoom,
    sendMessage: sendMessageCapped, // 🚀 USE CAPPED VERSION: Prevents inflight overload
    sendTypingStatus,
    loadMoreMessages,
    onMessage,
    onTyping,
    onError,
    isConnected,
    error,
    // 🚀 NEW: Queue management utilities
    getQueueStats,
    clearMessageQueue,
    getConnectionStatus
  };
}; 